import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { Nullable, Nullish } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { ObjectHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ObjectHelper";
import { WebApiClientService } from "@seriously-rad/seriouslyrad-framework-core/Server/WebApiClientService";
import { ServiceManager } from "@seriously-rad/seriouslyrad-framework-core/ServiceManager";
import { IClaimProcessInstanceItemResponse } from "../Server/ProcessDataService";
import { IProcessItemService } from "./ProcessItemService";
import { NAMESPACE } from "./_Namespace";

const
    services = new Map<string, () => Promise<Object>>();

export class ProcessItemServiceManager
{
    public static classNamespace = NAMESPACE + "ProcessItemServiceManager";
    public static className = "ProcessItemServiceManager";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    public static add<T extends IProcessItemService>(serviceId: string, loadImplementationInstance: () => Promise<T>): void
    {
        services.set(serviceId.toUpperCase(), loadImplementationInstance);
    }
    public static async getOptionalAsync<T extends IProcessItemService>(serviceId: string): Promise<Nullable<T>>
    {
        const
            instanceLoader = services.get(serviceId.toUpperCase());

        if (!instanceLoader)
            return null;

        const
            instance = await instanceLoader();

        return instance as Nullable<T>;
    }
    public static async getRequiredAsync<T extends IProcessItemService>(serviceId: string): Promise<T>
    {
        const
            instance = await this.getOptionalAsync(serviceId.toUpperCase());

        if (!instance)
            throw new Error(`Service \"${serviceId.toUpperCase()}\" not found`);

        return instance as T;
    }

    public static async cancelProcessInstanceItemAsync(processInstanceItemId: string)
    {
        await
            ServiceManager.getRequired(WebApiClientService)
                .createApiMethodRequest(
                    "DataService",
                    "CallDataService",
                    {
                        id: "b185975f-0e14-4944-b706-6353f0d67dc4",
                        operation: "CancelProcessInstanceItem",
                        data: ObjectHelper.toJson({
                            processInstanceItemId,
                            synchronousContinuation: true
                        })
                    }
                )
                .executeAsync();
    }
    public static async claimProcessInstanceItemAsync(processInstanceItemId: string): Promise<IClaimProcessInstanceItemResponse>
    {
        const
            data =
                await
                    ServiceManager.getRequired(WebApiClientService)
                        .createApiMethodRequest(
                            "DataService",
                            "CallDataService",
                            {
                                id: "b185975f-0e14-4944-b706-6353f0d67dc4",
                                operation: "ClaimProcessInstanceItem",
                                data: ObjectHelper.toJson({
                                    processInstanceItemId
                                })
                            }
                        )
                        .executeAsync();

        Assert.isNotNullOrUndefined(data, "data");

        return data as IClaimProcessInstanceItemResponse;
    }
    public static async completeProcessInstanceItemAsync(processInstanceItemId: string, data: any)
    {
        await
            ServiceManager.getRequired(WebApiClientService)
                .createApiMethodRequest(
                    "DataService",
                    "CallDataService",
                    {
                        id: "b185975f-0e14-4944-b706-6353f0d67dc4",
                        operation: "CompleteProcessInstanceItem",
                        data: ObjectHelper.toJson({
                            data: ObjectHelper.toJson(data ?? null),
                            processInstanceItemId,
                            synchronousContinuation: true
                        })
                    }
                )
                .executeAsync();
    }
    public static async executeProcessInstanceItemAsync(processInstanceItemId: string)
    {
        const
            processInstanceItem =
                await
                    ServiceManager.getRequired(WebApiClientService)
                        .createEntityGetRequest("ProcessInstanceItems", processInstanceItemId, ["entityId", "recordId"])
                        .executeAsync(),
            claimProcessInstanceItemResponse = await this.claimProcessInstanceItemAsync(processInstanceItemId),
            processItemService = await this.getRequiredAsync(claimProcessInstanceItemResponse.processItemServiceId);

        processItemService.entityId.set(processInstanceItem.entityId);
        processItemService.recordId.set(processInstanceItem.recordId);
        processItemService.processInstanceItemId.set(processInstanceItemId);
        processItemService.processItemServiceProperties.set(ObjectHelper.fromJson(claimProcessInstanceItemResponse.processItemServiceProperties));

        await processItemService.executeAsync();
    }
    public static async releaseProcessInstanceItemAsync(processInstanceItemId: string): Promise<void>
    {
        await
            ServiceManager.getRequired(WebApiClientService)
                .createApiMethodRequest("DataService", "CallDataService", { id: "b185975f-0e14-4944-b706-6353f0d67dc4", operation: "ReleaseProcessInstanceItem", data: ObjectHelper.toJson({ processInstanceItemId }) })
                .executeAsync();
    }
    public static async sendMessageAsync(messageTypeId: string, processVersionId?: Nullish<string>, processInstanceId?: Nullish<string>, processInstanceScopeId?: Nullish<string>, previousProcessInstanceItemId?: Nullish<string>, correlationKey?: Nullish<string>, payload?: Nullish<any>)
    {
        await
            ServiceManager.getRequired(WebApiClientService)
                .createApiMethodRequest(
                    "DataService",
                    "CallDataService",
                    {
                        id: "b185975f-0e14-4944-b706-6353f0d67dc4",
                        operation: "SendMessage",
                        data: ObjectHelper.toJson({
                            messageTypeId,
                            processVersionId,
                            processInstanceId,
                            processInstanceScopeId,
                            previousProcessInstanceItemId,
                            correlationKey,
                            payload,
                            synchronousContinuation: true
                        })
                    }
                )
                .executeAsync();
    }
}